import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {useCognitoAuth} from '@/app/modules/auth'
import {SHARED_FOLDER_ICON} from './constants'
import {shareErrorMessage} from './errors'
import {useCreateShare, useShareInvitations, useShareMembers, useStopShare} from './managementHooks'
import {OwnershipNote} from './OwnershipNote'
import {ShareActivityList} from './ShareActivityList'
import {shareFolderDisplayName} from './shareEligibility'
import {ShareInviteForm} from './ShareInviteForm'
import {ShareMembersList, SharePendingInvitations} from './ShareMembersList'
import type {ShareView} from './types'
import './sharing.scss'

type Tab = 'people' | 'activity' | 'about'

const TABS: {id: Tab; label: string}[] = [
  {id: 'people', label: 'People'},
  {id: 'activity', label: 'Activity'},
  {id: 'about', label: 'About ownership'},
]

/** E10, said where the owner decides to share. */
const FOLDER_RULES =
  "While the folder is shared, you can't rename or move it, or any folder above it. Stop sharing first if you need to."

export interface ShareFolderDialogProps {
  /** Relative to My Files, ending with `/`. */
  folderPath: string
  /** The folder's share when it is already shared; otherwise the dialog offers to share it. */
  share?: ShareView
  onHide: () => void
}

/**
 * "Share folder…" (P1-F5): shares a folder from My Files if it isn't yet, then manages it —
 * invitations, members and their access, activity, and Stop sharing. Owner only (D6).
 */
export const ShareFolderDialog: React.FC<ShareFolderDialogProps> = ({
  folderPath,
  share: initialShare,
  onHide,
}) => {
  const [share, setShare] = useState<ShareView | undefined>(initialShare)
  useEffect(() => setShare(initialShare), [initialShare])

  const name = share?.name || shareFolderDisplayName(folderPath)

  return (
    <Modal
      show
      onHide={onHide}
      size='lg'
      centered
      scrollable
      aria-labelledby='share-folder-dialog-title'
      dialogClassName='ob-sharing-dialog'
    >
      <Modal.Header closeButton>
        <Modal.Title id='share-folder-dialog-title' className='text-truncate'>
          <i className={`${SHARED_FOLDER_ICON} me-2`} aria-hidden='true' />
          Share “{name}”
        </Modal.Title>
      </Modal.Header>
      {share ? (
        <ManageShare share={share} onHide={onHide} />
      ) : (
        <StartSharing folderPath={folderPath} onShared={setShare} onHide={onHide} />
      )}
    </Modal>
  )
}

const StartSharing: React.FC<{
  folderPath: string
  onShared: (share: ShareView) => void
  onHide: () => void
}> = ({folderPath, onShared, onHide}) => {
  const create = useCreateShare()
  const [error, setError] = useState<string | null>(null)

  const start = () => {
    setError(null)
    create.mutate(folderPath, {
      onSuccess: (share) => onShared(share),
      onError: (err) => {
        setError(shareErrorMessage(err))
        getLogger().warn('Sharing a folder failed', err)
      },
    })
  }

  return (
    <>
      <Modal.Body>
        <p className='mb-3'>
          Invite people to view or edit this folder. They find it under <strong>Shared</strong> in
          their Files.
        </p>
        <OwnershipNote />
        <p className='text-muted small mt-3 mb-0'>{FOLDER_RULES}</p>
        {error && (
          <div className='alert alert-danger small mt-3 mb-0' role='alert'>
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type='button'
          className='btn btn-light'
          onClick={onHide}
          disabled={create.isLoading}
        >
          Cancel
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={start}
          disabled={create.isLoading}
          data-testid='sharing-start'
        >
          {create.isLoading ? 'Sharing…' : 'Share folder'}
        </button>
      </Modal.Footer>
    </>
  )
}

const ManageShare: React.FC<{share: ShareView; onHide: () => void}> = ({share, onHide}) => {
  const {currentUser} = useCognitoAuth()
  const [tab, setTab] = useState<Tab>('people')
  const [confirmStop, setConfirmStop] = useState(false)
  const [stopError, setStopError] = useState<string | null>(null)
  const members = useShareMembers(share.shareId)
  const invitations = useShareInvitations(share.shareId)
  const stop = useStopShare(share.shareId)

  const memberList = useMemo(() => members.data?.members ?? [], [members.data])
  const memberSubs = useMemo(() => new Set(memberList.map((m) => m.sub)), [memberList])
  const otherMembers = memberList.filter((m) => m.access !== 'owner').length
  const pending = invitations.data?.invitations ?? []

  const stopSharing = () => {
    setStopError(null)
    stop.mutate(undefined, {
      onSuccess: () => {
        toast.success(`“${share.name}” is no longer shared`, toastDefaultConfig)
        onHide()
      },
      onError: (err) => {
        setStopError(
          shareErrorMessage(err, {
            overrides: {
              share_moving:
                'This folder is being moved. Stop sharing it when the move has finished.',
            },
          })
        )
        getLogger().warn('Stop sharing failed', err)
      },
    })
  }

  if (confirmStop) {
    return (
      <>
        <Modal.Body>
          <h6 className='mb-3'>Stop sharing “{share.name}”?</h6>
          <ul className='mb-0'>
            <li>
              {otherMembers === 0
                ? 'Nobody else has joined this folder yet.'
                : `${otherMembers} ${
                    otherMembers === 1 ? 'member loses' : 'members lose'
                  } access.`}{' '}
              Links they already opened can keep working for up to 15 minutes.
            </li>
            <li>Pending invitations stop working.</li>
            <li>No files are moved or deleted. The folder stays in your My Files.</li>
          </ul>
          {stopError && (
            <div className='alert alert-danger small mt-3 mb-0' role='alert'>
              {stopError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => setConfirmStop(false)}
            disabled={stop.isLoading}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-danger'
            onClick={stopSharing}
            disabled={stop.isLoading}
            data-testid='sharing-stop-confirm'
          >
            {stop.isLoading ? 'Stopping…' : 'Stop sharing'}
          </button>
        </Modal.Footer>
      </>
    )
  }

  return (
    <>
      <Modal.Body>
        <p className='text-muted small mb-3'>
          {share.folderPath} ·{' '}
          {otherMembers === 0
            ? 'Not shared with anyone yet'
            : `Shared with ${otherMembers} ${otherMembers === 1 ? 'person' : 'people'}`}
        </p>
        <ul className='nav nav-tabs mb-4' role='tablist'>
          {TABS.map((t) => (
            <li key={t.id} className='nav-item' role='presentation'>
              <button
                type='button'
                role='tab'
                id={`share-tab-${t.id}`}
                aria-selected={tab === t.id}
                aria-controls={`share-panel-${t.id}`}
                className={`nav-link${tab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>

        <div role='tabpanel' id={`share-panel-${tab}`} aria-labelledby={`share-tab-${tab}`}>
          {tab === 'people' && (
            <>
              <ShareInviteForm shareId={share.shareId} memberSubs={memberSubs} />

              {pending.length > 0 && (
                <section className='mt-4'>
                  <h6 className='mb-2'>Pending invitations</h6>
                  <SharePendingInvitations shareId={share.shareId} invitations={pending} />
                </section>
              )}

              <section className='mt-4'>
                <h6 className='mb-2'>Members</h6>
                {members.isLoading ? (
                  <div className='text-muted small'>Loading members…</div>
                ) : members.isError ? (
                  <div className='alert alert-warning small mb-0' role='alert'>
                    {shareErrorMessage(members.error)}
                  </div>
                ) : (
                  <ShareMembersList
                    shareId={share.shareId}
                    members={memberList}
                    currentSub={currentUser?.sub}
                  />
                )}
              </section>
            </>
          )}
          {tab === 'activity' && <ShareActivityList shareId={share.shareId} members={memberList} />}
          {tab === 'about' && (
            <>
              <OwnershipNote variant='plain' />
              <p className='text-muted small mt-3 mb-0'>{FOLDER_RULES}</p>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className='justify-content-between'>
        <button
          type='button'
          className='btn btn-light-danger'
          onClick={() => setConfirmStop(true)}
          data-testid='sharing-stop'
        >
          Stop sharing
        </button>
        <button type='button' className='btn btn-primary' onClick={onHide}>
          Done
        </button>
      </Modal.Footer>
    </>
  )
}
